import last from 'lodash/last';
import pick from 'lodash/pick';
import { extractKnowledgeBaseArticleId, extractMacroArticleId, getKbOrMacroResultSnippet } from '../util/kbUtil';
import { postAnalyticsEvent, postAnalyticsEventSimple, getParsePointersForSelectedTicket } from './common';
import { _getState } from '../store';
import {
  getSelectedSearchResult,
  getSelectedSearchResultIndex,
  getSearchId,
  getSelectedSearchResultType,
  extractSelectedWizardMacro,
  showingMacroWizard,
  getDecisionNodeAtPath,
  extractCurrentDecisionTreePath,
  extractCurrentMacroWizardPath,
  getActionsOfAncestorsOfDecisionTree,
  getBrandOfSelectedTicket,
  getSearchTypeOfSelectedAction,
} from '../extractFromState';
import { getSearchAllQuery, getSelectedSubscriptionList } from '../selectors/tools';
import * as searchTypes from '../constants/searchTypes';
import * as util from '../util';
import { DECISION_NODE_ACTIONS } from '../constants/decisionTree';

const MACRO_SELECTED = 'MACRO_SELECTED';
const MACRO_COPIED = 'MACRO_COPIED';
const AGENT_ASSIST_MACRO_COPIED = 'AGENT_ASSIST_MACRO_COPIED';

const KNOWLEDGE_BASE_ARTICLE_SELECTED = 'KNOWLEDGE_BASE_ARTICLE_SELECTED';
const HISTORICAL_TICKET_SELECTED = 'HISTORICAL_TICKET_SELECTED';

const SEARCH_QUERY_SUBMITTED = 'SEARCH_QUERY_SUBMITTED';
const SEARCH_RESULT_CLOSED = 'SEARCH_RESULT_CLOSED';
const SEARCH_RESULT_FEEDBACK_SUBMITTED = 'SEARCH_RESULT_FEEDBACK_SUBMITTED';

const ORDER_SEARCH_QUERY_SUBMITTED = 'ORDER_SEARCH_QUERY_SUBMITTED';
const ORDER_SEARCH_SET_ACTION = 'ORDER_SEARCH_SET_ACTION';

const MACRO_WIZARD_CATEGORY_SELECTED = 'MACRO_WIZARD_CATEGORY_SELECTED';
const MACRO_WIZARD_SUGGESTION_SELECTED = 'MACRO_WIZARD_SUGGESTION_SELECTED';
const MACRO_WIZARD_MACRO_SELECTED = 'MACRO_WIZARD_MACRO_SELECTED';
const ESCALATION_CLICK_BASED_ON_ALERT = 'ESCALATION_CLICK_BASED_ON_ALERT';

const MACRO_WIZARD_JUMP_TO_TAG_PATH = 'MACRO_WIZARD_JUMP_TO_TAG_PATH';
const MACRO_WIZARD_BREADCRUMB_CLICK = 'MACRO_WIZARD_BREADCRUMB_CLICK';

const DECISION_TREE_START = 'DECISION_TREE_START';
const DECISION_TREE_BREADCRUMB_CLICK = 'DECISION_TREE_BREADCRUMB_CLICK';
const DECISION_TREE_CASE_NOT_HANDELED = 'DECISION_TREE_CASE_NOT_HANDELED';
const DECISION_TREE_QUESTION_ANSWERED = 'DECISION_TREE_QUESTION_ANSWERED';
const DECISION_TREE_PATH_CHANGED = 'DECISION_TREE_PATH_CHANGED';

const DECISION_TREE_SUBSCRIPTION_SEARCH_QUERIED = 'DECISION_TREE_SUBSCRIPTION_SEARCH_QUERIED';
const DECISION_TREE_SUBSCRIPTION_SELECTED = 'DECISION_TREE_SUBSCRIPTION_SELECTED';
const DECISION_TREE_SUBSCRIPTION_CANCELLED = 'DECISION_TREE_SUBSCRIPTION_CANCELLED';
const DECISION_TREE_TRACKING_LINK_CLICKED = 'DECISION_TREE_TRACKING_LINK_CLICKED';

const DECISION_TREE_TEXT_COPIED = 'DECISION_TREE_TEXT_COPIED';
const DECISION_TREE_COPY_BUTTON_CLICKED = 'DECISION_TREE_COPY_BUTTON_CLICKED';
const DECISION_TREE_UNHANDLED_SUBSCRIPTION_ERROR = 'DECISION_TREE_UNHANDLED_SUBSCRIPTION_ERROR';

const DECISION_TREE_SELECT_LINE_ITEMS_OF_ORDER = 'DECISION_TREE_SELECT_LINE_ITEMS_OF_ORDER';
const DECISION_TREE_ORDER_STATUS_SELECTION_COMPLETE = 'DECISION_TREE_ORDER_STATUS_SELECTION_COMPLETE';
const DECISION_TREE_ORDER_SHIPPING_REFUNDED = 'DECISION_TREE_ORDER_SHIPPING_REFUNDED';

const DECISION_TREE_STORE_CREDIT_ISSUED = 'DECISION_TREE_STORE_CREDIT_ISSUED';

const DECISION_TREE_ITEM_REFUNDED = 'DECISION_TREE_ITEM_REFUNDED';
const DECISION_TREE_ITEM_PARTIALLY_REFUNDED = 'DECISION_TREE_ITEM_PARTIALLY_REFUNDED';

const DECISION_TREE_UPDATED_SUBSCRIPTION_FREQUENCY = 'DECISION_TREE_UPDATED_SUBSCRIPTION_FREQUENCY';
const DECISION_TREE_ITEM_REPLACED_WITH_COUPON = 'DECISION_TREE_ITEM_REPLACED_WITH_COUPON';

const DECISION_TREE_RETURN_SEARCH_QUERY_SUBMITTED = 'DECISION_TREE_RETURN_SEARCH_QUERY_SUBMITTED';
const DECISION_TREE_RETURN_SEARCH_ACTION_SET = 'DECISION_TREE_RETURN_SEARCH_ACTION_SET';

const { SEARCH_TICKETS, SEARCH_MACROS, SEARCH_KB } = searchTypes;

export const SEARCH_ACTION_SOURCES = {
  MAIN_LIST: 'MAIN_LIST',
  ARROW: 'ARROW',
  COPY_BUTTON: 'COPY_BUTTON',
  FREE_COPY: 'FREE_COPY',
};

export const trackSearchResultFeedbackEvent = async ({
  searchId,
  conversationId,
  simplrAccountId,
  algoliaObjectId,
  searchResultType,
  searchResultFeedback,
}) => {
  const parsePointers = {
    conversationPtr: conversationId,
    simplrAccountPtr: simplrAccountId,
  };

  const params = {
    searchId,
    algoliaObjectId,
    searchResultType,
    searchResultFeedback,
  };

  await postAnalyticsEvent(SEARCH_RESULT_FEEDBACK_SUBMITTED, parsePointers, params);
};

// Wrapper functions for tracking events via segment in addition to created an analytic event in AnalyticsEvent table

export const trackSearchItemSelected = async (source = SEARCH_ACTION_SOURCES.MAIN_LIST) => {
  const resultType = getSelectedSearchResultType(_getState());
  const searchResult = getSelectedSearchResult(_getState());
  let eventName;

  const params = {
    searchId: getSearchId(_getState()),
    algoliaObjectId: searchResult.objectID,
    searchResultRank: getSelectedSearchResultIndex(_getState()),
    searchResultSnippet: getKbOrMacroResultSnippet(searchResult),
    searchQuery: getSearchAllQuery(_getState()),
    source,
  };

  switch (resultType) {
    case SEARCH_MACROS:
      eventName = MACRO_SELECTED;
      params.macroArticleId = extractMacroArticleId(searchResult.objectID);
      break;
    case SEARCH_KB:
      eventName = KNOWLEDGE_BASE_ARTICLE_SELECTED;
      params.knowledgeBaseArticleId = extractKnowledgeBaseArticleId(searchResult.objectID);
      break;
    case SEARCH_TICKETS:
      eventName = HISTORICAL_TICKET_SELECTED;
      params.historicalTicketId = searchResult.objectID;
      break;
    default:
      // not handling other cases right now
      return;
  }

  await postAnalyticsEventSimple(eventName, params);
};

export const trackSearchQuerySubmitted = async (searchId, searchQuery, conversationId, simplrAccountId) => {
  const parsePointers = {
    conversationPtr: conversationId,
    simplrAccountPtr: simplrAccountId,
  };

  const params = {
    searchId,
    searchQuery,
  };

  await postAnalyticsEvent(SEARCH_QUERY_SUBMITTED, parsePointers, params);
};

export const trackSearchResultClosed = async ({
  searchId,
  conversationId,
  simplrAccountId,
  algoliaObjectId,
  searchResultType,
}) => {
  const parsePointers = {
    conversationPtr: conversationId,
    simplrAccountPtr: simplrAccountId,
  };

  const params = {
    searchId,
    algoliaObjectId,
    searchResultType,
  };

  await postAnalyticsEvent(SEARCH_RESULT_CLOSED, parsePointers, params);
};

export const trackOrderSearchQuerySubmitted = async ({ action, searchQuery }) => {
  const parsePointers = getParsePointersForSelectedTicket();

  const params = {
    action,
    searchQuery,
  };

  await postAnalyticsEvent(ORDER_SEARCH_QUERY_SUBMITTED, parsePointers, params);
};

export const trackOrderSearchSetAction = async ({ action }) => {
  const parsePointers = getParsePointersForSelectedTicket();

  const params = {
    action,
  };

  await postAnalyticsEvent(ORDER_SEARCH_SET_ACTION, parsePointers, params);
};

export const trackMacroWizardCategorySelected = async (tagPath) => {
  const splitPath = util.splitTagPath(tagPath);
  const category = last(splitPath);
  const depth = splitPath.length;
  await postAnalyticsEventSimple(MACRO_WIZARD_CATEGORY_SELECTED, { category, depth });
};

export const trackMacroWizardSuggestionSelected = async (selectedTag, recommendedMLTags) => {
  await postAnalyticsEventSimple(MACRO_WIZARD_SUGGESTION_SELECTED, { selectedTag, recommendedMLTags });
};

const _getMacroAnalyticsParams = (source, macroArticle = null) => {
  const state = _getState();
  const resultType = getSelectedSearchResultType(state);
  let searchResult, macroArticleId;
  let fromMacroWizard = showingMacroWizard(state);
  const userInDecisionTree = !!getDecisionNodeAtPath(state) && getSearchTypeOfSelectedAction(state) === 'MACRO_WIZARD';
  if (fromMacroWizard) {
    // decision tree macro articles are not stored in state
    searchResult = macroArticle || extractSelectedWizardMacro(state);
    macroArticleId = searchResult.objectId;
  } else if (resultType === SEARCH_MACROS) {
    searchResult = getSelectedSearchResult(state);
    macroArticleId = extractMacroArticleId(searchResult.objectID);
  } else {
    // shouldn't happen but if it does just return empty params
    return {};
  }

  const { category, fullSubheading } = searchResult;
  const params = {
    category,
    fullSubheading,
    macroArticleId,
    source,
    fromMacroWizard,
    userInDecisionTree,
  };

  return params;
};

export const trackEscalationClickBasedOnAlert = (businessAlertPtr) => {
  const params = {
    businessAlertPtr,
  };
  postAnalyticsEventSimple(ESCALATION_CLICK_BASED_ON_ALERT, params);
};

export const trackMacroWizardMacroSelected = async (source = SEARCH_ACTION_SOURCES.MAIN_LIST) => {
  const params = _getMacroAnalyticsParams(source);
  await postAnalyticsEventSimple(MACRO_WIZARD_MACRO_SELECTED, params);
};

export const trackMacroTextCopied = async (source = SEARCH_ACTION_SOURCES.COPY_BUTTON, macroArticle) => {
  const macroParams = _getMacroAnalyticsParams(source, macroArticle);
  const { params: decisionTreeParams } = _getDecisionTreeAnalyticsParams();
  const params = { ...macroParams, ...decisionTreeParams };

  await postAnalyticsEventSimple(MACRO_COPIED, params);
};

export const trackAgentAssistMacroTextCopied = async (macroText) => {
  const { params: decisionTreeParams } = _getDecisionTreeAnalyticsParams();
  const params = { macroText, ...decisionTreeParams };

  await postAnalyticsEventSimple(AGENT_ASSIST_MACRO_COPIED, params);
};

export const trackMacroWizardJumpToTagPath = async (tagPath) => {
  const params = { tagPath };
  await postAnalyticsEventSimple(MACRO_WIZARD_JUMP_TO_TAG_PATH, params);
};

export const trackMacroWizardBreadcrumbClick = async (tagPath) => {
  const splitPath = util.splitTagPath(tagPath);
  const category = last(splitPath) || 'ALL'; // track the category as "ALL"
  const depth = splitPath.length;
  await postAnalyticsEventSimple(MACRO_WIZARD_BREADCRUMB_CLICK, { category, depth });
};

/****
 *
 * Decision tree analytics
 *
 ****/

const _getDecisionTreeAnalyticsParams = (params = {}) => {
  const decisionNode = getDecisionNodeAtPath(_getState());

  // pull the action out so we don't have to do a join for this data
  params.decisionTreePath = extractCurrentDecisionTreePath(_getState());
  params.macroWizardPath = extractCurrentMacroWizardPath(_getState());
  params.decisionNode = decisionNode;
  params.ticketBrand = getBrandOfSelectedTicket(_getState());
  params.treeBrand = getBrandOfSelectedTicket(_getState());

  const ancestorActions = getActionsOfAncestorsOfDecisionTree(_getState());

  // add sub params if using subscription flow
  if (ancestorActions.indexOf(DECISION_NODE_ACTIONS.SEND_MACRO_AND_CANCEL_SUBSCRIPTION) > -1) {
    const subscriptionList = getSelectedSubscriptionList(_getState());
    if (subscriptionList.length > 0) {
      // there could be multiple subscriptions but we can log the first subscription since we are already storing that
      const subscription = subscriptionList[0];
      params.subscription = pick(subscription, [
        'status',
        'sku',
        'product_title',
        'id',
        'created_at',
        'price',
        'customer_id',
      ]);
      params.subscriptionIdList = subscriptionList.map((subscriptionElem) => subscriptionElem.id);
    }
  }

  // custom pointers
  const customParsePointers = [];
  // new trees do not have decisionNode ids - nodes are written directly on tree
  if (decisionNode && decisionNode.decisionNodeId) {
    customParsePointers.push({ className: 'DecisionNode', id: decisionNode.decisionNodeId, name: 'decisionNodePtr' });
  }

  return { params, customParsePointers };
};

export const trackDecisionTreeStart = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_START, {}, params, customParsePointers);
};

export const trackDecisionTreePathChanged = async (choice) => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams({ choice });
  await postAnalyticsEvent(DECISION_TREE_PATH_CHANGED, {}, params, customParsePointers);
};

export const trackDecisionTreeBreadcrumbClick = async (tagPath) => {
  const splitPath = util.splitTagPath(tagPath);
  const depth = splitPath.length;
  const { params, customParsePointers } = _getDecisionTreeAnalyticsParams({ depth });
  await postAnalyticsEvent(DECISION_TREE_BREADCRUMB_CLICK, {}, params, customParsePointers);
};

export const trackDecisionTreeCaseNotHandled = async (error) => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams({ error });
  await postAnalyticsEvent(DECISION_TREE_CASE_NOT_HANDELED, {}, params, customParsePointers);
};

export const trackDecisionNodeQuestionAnswered = async (choice) => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams({ choice });
  await postAnalyticsEvent(DECISION_TREE_QUESTION_ANSWERED, {}, params, customParsePointers);
};

export const trackDecisionTreeSubscriptionSearch = async (inputParams) => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams(inputParams);
  await postAnalyticsEvent(DECISION_TREE_SUBSCRIPTION_SEARCH_QUERIED, {}, params, customParsePointers);
};

export const trackDecisionTreeSubscriptionSelected = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_SUBSCRIPTION_SELECTED, {}, params, customParsePointers);
};

export const trackDecisionTreeTextCopied = async (source = SEARCH_ACTION_SOURCES.COPY_BUTTON) => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams({ source });
  await postAnalyticsEvent(DECISION_TREE_TEXT_COPIED, {}, params, customParsePointers);
};

export const trackDecisionTreeItemRefunded = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_ITEM_REFUNDED, {}, params, customParsePointers);
};

export const trackDecisionTreeOrderShippingRefunded = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_ORDER_SHIPPING_REFUNDED, {}, params, customParsePointers);
};

export const trackDecisionTreeItemPartiallyRefunded = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_ITEM_PARTIALLY_REFUNDED, {}, params, customParsePointers);
};

export const trackDecisionTreeItemReplacedWithCoupon = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_ITEM_REPLACED_WITH_COUPON, {}, params, customParsePointers);
};

export const trackDecisionTreeUpdatedFrequency = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_UPDATED_SUBSCRIPTION_FREQUENCY, {}, params, customParsePointers);
};

export const trackDecisionTreeSubscriptionCancelled = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_SUBSCRIPTION_CANCELLED, {}, params, customParsePointers);
};

export const trackDecisionTreeStoreCreditIssued = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_STORE_CREDIT_ISSUED, {}, params, customParsePointers);
};

export const trackDecisionTreeTrackingLinkClicked = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_TRACKING_LINK_CLICKED, {}, params, customParsePointers);
};

export const trackDecisionTreeClickCopyButton = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_COPY_BUTTON_CLICKED, {}, params, customParsePointers);
};

export const trackDecisionTreeUnhandledSubscriptionError = async (error) => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams({ error });
  await postAnalyticsEvent(DECISION_TREE_UNHANDLED_SUBSCRIPTION_ERROR, {}, params, customParsePointers);
};

export const trackDecisionTreeOrderStatusLineItemsSelected = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_SELECT_LINE_ITEMS_OF_ORDER, {}, params, customParsePointers);
};

export const trackDecisionTreeOrderStatusSelectionComplete = async () => {
  let { params, customParsePointers } = _getDecisionTreeAnalyticsParams();
  await postAnalyticsEvent(DECISION_TREE_ORDER_STATUS_SELECTION_COMPLETE, {}, params, customParsePointers);
};

export const trackReturnSearchQuerySubmitted = async ({ action, searchQuery }) => {
  const parsePointers = getParsePointersForSelectedTicket();

  const params = {
    action,
    searchQuery,
  };

  await postAnalyticsEvent(DECISION_TREE_RETURN_SEARCH_QUERY_SUBMITTED, parsePointers, params);
};

export const trackReturnSearchSetAction = async ({ action }) => {
  const parsePointers = getParsePointersForSelectedTicket();

  const params = {
    action,
  };

  await postAnalyticsEvent(DECISION_TREE_RETURN_SEARCH_ACTION_SET, parsePointers, params);
};
